<template>
  <div
    id="checklists"
    :class="{ 'checklists': true, 'is-loading': !loaded, 'save-in-progress': orderSaveInProgress }">
    <fieldset class="mb-0">
      <legend>Checklist items</legend>

      <d-alert
        v-if="referenceId === 0"
        theme="light"
        show
        class="text-center">
        You will be able to see and add checklist items after saving this {{ itemName }}.
      </d-alert>

      <d-alert
        v-if="loadError"
        theme="warning"
        show
        class="text-center">
        An error occurred during loading checklist items.
      </d-alert>

      <d-row
        v-if="loaded && !loadError && referenceId !== 0"
        class="pr-3 mb-3">
        <d-button
          theme="success"
          @click.prevent="addItem"
          outline
          class="ml-auto">
          Add item
        </d-button>
        <d-button
          theme="warning"
          @click.prevent="addItemsFromTemplate"
          outline
          class="ml-2">
          Add items from template
        </d-button>
      </d-row>

      <d-row
        v-if="loaded && !loadError && referenceId !== 0"
        class="pl-3 pr-3">
        <table class="table">
          <thead>
            <tr>
              <th style="width: 50px;">Order</th>
              <th>Item</th>
              <th style="width: 140px;">Actions</th>
            </tr>
          </thead>
          <draggable
            v-model="items"
            tag="tbody">
            <checklist-list-item
              v-for="(itemData, index) of items"
              :key="'checklist-list-item-' + index"
              :itemData="itemData" />
          </draggable>
        </table>
      </d-row>

      <d-alert
        v-if="loaded && !loadError && referenceId !== 0 && items.length === 0"
        theme="light"
        show
        class="text-center">
        There are no checklist items for this {{ itemName }}.
      </d-alert>
    </fieldset>
  </div>
</template>

<script>
import debounce from 'lodash.debounce';
import FormUtils from './../../utils/FormUtils.js';
import CheckListsItem from './CheckListsItem.vue';
import vueDraggable from 'vuedraggable';

export default {
  name: 'checklist-list',
  components: {
    'checklist-list-item': CheckListsItem,
    'draggable': vueDraggable
  },
  props: {
    referenceType: {
      type: String,
      required: true
    },
    referenceId: {
      type: Number,
      required: true
    },
    itemName: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      loaded: false,
      loadError: false,
      orderSaveInProgress: false,
      items: []
    };
  },
  watch: {
    'items': function (newValue, oldValue) {
      if (newValue.length !== oldValue.length) {
        return;
      }

      if (this.items.length > 0) {
        this.debouncedSaveOfOrdering();
      }
    }
  },
  mounted () {
    if (this.referenceId === 0) {
      this.loaded = true;
    } else {
      this.loadItems();
    }

    this.$bus.$on('checklist-list-reload', this.reload);
  },
  methods: {
    loadItems () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/check-lists/items',
        params: {
          pagination: 0,
          where: {
            reference_id: this.referenceId,
            reference_type: this.referenceType
          }
        },
        loadedKey: 'loaded',
        outputKey: 'items',
        errorKey: 'loadError',
        successAction: () => {
          if (this.referenceType === 'FLIGHT') {
            this.$bus.$emit('flight-details-reload', this.referenceId);
          }
        }
      });
    },
    addItem () {
      this.$bus.$emit('show-checklist-item-popup', 'add', this.referenceType, this.referenceId);
    },
    addItemsFromTemplate () {
      this.$bus.$emit('show-checklist-templates-popup', this.referenceType, this.referenceId);
    },
    reload () {
      this.loaded = false;
      this.loadItems();
    },
    // eslint-disable-next-line
    debouncedSaveOfOrdering: debounce(function () {
      this.saveItemsOrder();
    }, 1000),
    saveItemsOrder () {
      this.orderSaveInProgress = true;

      FormUtils.submitInBackground({
        $http: this.$http,
        endpoint: '/check-lists/set-ordering',
        formData: {
          items: this.items.map(item => item.id),
          reference_id: this.referenceId,
          reference_type: this.referenceType
        },
        errorTitle: 'An error occurred',
        errorText: 'An error occurred during ordering change. Please try again.',
        successAction: () => {
          this.orderSaveInProgress = false;
        }
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('checklist-list-reload', this.reload);
  }
}
</script>

<style lang="scss" scoped>
.checklists {
  margin: 30px 0;
  position: relative;

  &.save-in-progress,
  &.is-loading {
    fieldset {
      min-height: 120px;
    }

    &:after {
      background: rgba(255, 255, 255, .66);
      content: "";
      height: 100%;
      left: 0;
      position: absolute;
      top: 0;
      width: 100%;
    }

    &:before {
      content: "Loading data...";
      left: 50%;
      position: absolute;
      top: 50%;
      transform: translateX(-50%) translateY(-50%);
      z-index: 1;
    }
  }

  &.save-in-progress {
    &:before {
      content: "Save in progress...";
    }
  }
}
</style>
